/**
 * SharedMediaManager — Acquires local media once and shares tracks across multiple peer connections.
 *
 * The same MediaStreamTrack can be added to multiple RTCPeerConnections.
 * The browser encodes once — no need to clone tracks.
 *
 * No React dependencies — pure JS class.
 */

export class SharedMediaManager {
  /**
   * @param {Object} [options]
   * @param {boolean} [options.syntheticVideo=false] - Also put a black video track into the
   *   synthetic stream. Off by default: the robots never consume browser video, and a video
   *   m-line in every offer only costs bandwidth and negotiation time.
   */
  constructor(options = {}) {
    this._syntheticVideo = options.syntheticVideo ?? false;
    this._stream = null;
    this._audioEnabled = true;
    this._videoEnabled = false;
    this._audioContext = null;
    this._oscillator = null;
    this._silentAudioTrack = null;
    this._blackVideoInterval = null;
    this._micStream = null; // the user's real microphone (acquireMicrophone), apart from the synthetic stream
  }

  /**
   * Check if WebRTC media is available in this browser context.
   */
  get mediaAllowed() {
    return typeof navigator !== 'undefined'
      && typeof navigator.mediaDevices !== 'undefined'
      && !!navigator.mediaDevices;
  }

  /**
   * Current local MediaStream (or null).
   */
  get stream() { return this._stream; }

  get audioEnabled() { return this._audioEnabled; }
  get videoEnabled() { return this._videoEnabled; }

  /**
   * Acquire local media via getUserMedia. Called once, shared across all peers.
   * @param {MediaStreamConstraints} constraints - { audio, video }
   * @returns {Promise<MediaStream|null>}
   */
  async acquire(constraints = { audio: false, video: false }) {
    if (!constraints.audio && !constraints.video) {
      // No media needed — create synthetic streams
      return this._createSyntheticStream();
    }

    if (!this.mediaAllowed) {
      throw new Error("Media devices not available. Requires HTTPS.");
    }

    // Stop previous stream
    this.release();

    try {
      this._stream = await navigator.mediaDevices.getUserMedia(constraints);
      return this._stream;
    } catch (err) {
      console.error("[SharedMediaManager] getUserMedia failed:", err);
      // Fallback to synthetic stream
      return this._createSyntheticStream();
    }
  }

  /**
   * Stop all tracks and release the stream.
   */
  release() {
    if (this._stream) {
      this._stream.getTracks().forEach((track) => track.stop());
      this._stream = null;
    }
    this._stopSilentAudio();
    this._stopBlackVideo();
    this.releaseMicrophone();
  }

  /**
   * Get the current stream for sharing with a peer connection.
   * Returns the same stream instance — tracks are shared, not cloned.
   */
  getStream() {
    return this._stream;
  }

  /**
   * Live track of the user's real microphone (acquireMicrophone), or null.
   * It is kept apart from the synthetic stream: every peer with a speaker
   * offers audio with the silent track, and the microphone is swapped in per
   * peer with replaceTrack (PeerConnection.setMicrophoneEnabled).
   * @returns {MediaStreamTrack|null}
   */
  getMicrophoneTrack() {
    const track = this._micStream?.getAudioTracks()[0];
    return track && track.readyState === "live" ? track : null;
  }

  /**
   * Open the user's microphone (getUserMedia prompt on first use). Unlike
   * acquire(), a denied or missing microphone rejects — the caller must not
   * believe it is sending audio.
   * @returns {Promise<MediaStreamTrack>} the live microphone track
   */
  async acquireMicrophone() {
    const existing = this.getMicrophoneTrack();
    if (existing) return existing;
    this.releaseMicrophone();
    if (!this.mediaAllowed) {
      throw new Error("Microphone not available (media devices require HTTPS)");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    const track = stream.getAudioTracks()[0];
    if (!track) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error("Microphone stream has no audio track");
    }
    this._micStream = stream;
    return track;
  }

  /** Stop the real microphone (the browser's recording indicator goes off). */
  releaseMicrophone() {
    if (this._micStream) {
      this._micStream.getTracks().forEach((track) => track.stop());
      this._micStream = null;
    }
  }

  /**
   * Toggle audio on/off for the shared stream.
   * Affects all peer connections using this stream's audio track.
   */
  toggleAudio(enabled) {
    this._audioEnabled = enabled;
    if (this._stream) {
      this._stream.getAudioTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Toggle video on/off for the shared stream.
   * Affects all peer connections using this stream's video track.
   */
  toggleVideo(enabled) {
    this._videoEnabled = enabled;
    if (this._stream) {
      this._stream.getVideoTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Enumerate available input devices.
   * @returns {Promise<{ cameras: MediaDeviceInfo[], microphones: MediaDeviceInfo[] }>}
   */
  async enumerateDevices() {
    if (!this.mediaAllowed) return { cameras: [], microphones: [] };
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return {
        cameras: devices.filter((d) => d.kind === "videoinput"),
        microphones: devices.filter((d) => d.kind === "audioinput"),
      };
    } catch (err) {
      console.error("[SharedMediaManager] enumerateDevices failed:", err);
      return { cameras: [], microphones: [] };
    }
  }

  /**
   * Create a synthetic stream with silent audio (+ black video when enabled).
   * Used when real media is not needed or not available.
   */
  _createSyntheticStream() {
    const stream = new MediaStream();

    const silentTrack = this._getSilentAudioTrack();
    if (silentTrack) stream.addTrack(silentTrack);

    if (this._syntheticVideo) {
      const blackStream = this._getBlackVideoStream();
      if (blackStream) {
        blackStream.getVideoTracks().forEach((t) => stream.addTrack(t));
      }
    }

    this._stream = stream;
    return stream;
  }

  /**
   * Generate a near-silent audio track via WebAudio API.
   */
  _getSilentAudioTrack() {
    this._stopSilentAudio();
    try {
      this._audioContext = new AudioContext();
      this._oscillator = this._audioContext.createOscillator();
      const gainNode = this._audioContext.createGain();

      this._oscillator.connect(gainNode);
      const dest = this._audioContext.createMediaStreamDestination();
      gainNode.connect(dest);
      this._oscillator.start();

      gainNode.gain.value = 0.01;

      this._silentAudioTrack = dest.stream.getAudioTracks()[0];
      return this._silentAudioTrack;
    } catch (e) {
      console.error("[SharedMediaManager] Failed to create silent audio:", e);
      return null;
    }
  }

  _stopSilentAudio() {
    if (this._oscillator) {
      try { this._oscillator.stop(); this._oscillator.disconnect(); } catch (e) { /* */ }
      this._oscillator = null;
    }
    if (this._silentAudioTrack) {
      try { this._silentAudioTrack.stop(); } catch (e) { /* */ }
      this._silentAudioTrack = null;
    }
    if (this._audioContext && this._audioContext.state !== "closed") {
      try { this._audioContext.close(); } catch (e) { /* */ }
      this._audioContext = null;
    }
  }

  /**
   * Generate a black video stream via canvas.
   * Sends a black frame every 3 seconds to keep the connection alive.
   */
  _getBlackVideoStream({ width = 320, height = 240 } = {}) {
    this._stopBlackVideo();
    try {
      const canvas = Object.assign(document.createElement("canvas"), { width, height });
      const ctx = canvas.getContext("2d");
      const stream = canvas.captureStream();

      this._blackVideoInterval = setInterval(() => {
        ctx.fillRect(0, 0, width, height);
      }, 3000);

      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => this._stopBlackVideo();
      }

      return stream;
    } catch (e) {
      console.error("[SharedMediaManager] Failed to create black video:", e);
      return null;
    }
  }

  _stopBlackVideo() {
    if (this._blackVideoInterval) {
      clearInterval(this._blackVideoInterval);
      this._blackVideoInterval = null;
    }
  }

  /**
   * Full teardown.
   */
  destroy() {
    this.release();
  }
}
